<script setup lang="ts">
import HeartIcon from "~/components/Icons/HeartIcon.vue";
import EyeIcon from "~/components/Icons/EyeIcon.vue";
import PostClaimButton from "~/components/PostClaimButton.vue";
import TweetDetailReward from "~/components/TweetDetailReward.vue";
import { formatNiceDate } from "~/utils/dateTime";
import { useQuery } from "@tanstack/vue-query";
import { TweetRewardState } from "evangelist-common/enums";
import ForumIcon from "../Icons/ForumIcon.vue";
import type { CampaignListItem } from "~/common/Types";
import { AnalyticsTweetType } from "evangelist-db-schema/api";
import { formatTweetText } from "evangelist-common/tweetHelpers";

interface Props {
  tweetId: string;
  forCampaignUuid: string | null;
  forCampaignWeekId: string | null;
}
const props = defineProps<Props>();
const emits = defineEmits(["close"]);

const { $trpc } = useNuxtApp();
const {
  data: tweet,
  isLoading,
  refetch: getTweet,
} = useQuery({
  queryKey: ["getTweetDetails", props.tweetId],
  queryFn: () =>
    $trpc.getTweetDetail.query({
      tweetId: props.tweetId,
      forCampaignUuid: props.forCampaignUuid,
    }),
});

const campaign = computed(() => {
  return (
    tweet.value?.campaigns.find(
      (c) => c.campaignUuid === props.forCampaignUuid,
    ) ?? tweet.value?.campaigns[0]
  );
});

const state = computed(() => {
  return tweet.value?.state;
});

const campaigns = computed(() => {
  return tweet.value?.campaigns ?? [];
});

const tweetLinkCopied = ref(false);

function copyTweetLink() {
  const tweetId = tweet.value?.id;
  const url = `https://twitter.com/i/web/status/${tweetId}`;

  trackEvent("user_clipboard_post_url_saved", {
    trigger: "button_post_detail_sidebar_time_via_evangelist_clicked",
    post_id: tweetId,
  });

  navigator.clipboard.writeText(url).then(() => {
    tweetLinkCopied.value = true;
    setTimeout(() => {
      tweetLinkCopied.value = false;
    }, 2000);
  });
}

function isHighestRewardedCampaign(index: number) {
  if (tweet.value === undefined) {
    return false;
  }

  if (tweet.value.campaigns.length <= 1) {
    return false;
  }

  if (tweet.value.state === TweetRewardState.NotRewarded) {
    return false;
  }

  return index === 0;
}

function close() {
  trackEvent("ui_post_detail_sidebar_closed", {
    trigger: "button_post_detail_sidebar_dismiss_clicked",
    post_id: tweet.value?.id,
  });

  emits("close");
}

function getUserTopNText(campaign?: CampaignListItem) {
  if (!campaign || campaign.userTopN === undefined) {
    return "";
  }

  return `(${campaign.userTopN})`;
}

const processedTweetContent = computed(() => {
  const formatted = formatTweetText(tweet.value?.content ?? "");
  return addTrackingAttributesToHtml(formatted);
});

onMounted(() => {
  trackEvent("ui_post_detail_sidebar_opened", {
    trigger: "tile_post_clicked",
    post_id: tweet.value?.id,
  });
});

onBeforeUnmount(() => {
  trackEvent("ui_post_detail_sidebar_closed", {
    trigger: "button_post_detail_sidebar_dismiss_clicked",
    post_id: tweet.value?.id,
  });
});
</script>

<template>
  <div v-if="tweet" class="font-semibold text-sm">
    <div class="flex items-center gap-2 mb-8">
      <span class="text-black/50">Posted by</span>
      <img
        v-if="tweet?.authorProfilePicUrl"
        :src="tweet.authorProfilePicUrl"
        class="w-6 h-auto rounded-full"
        alt=""
      />
      <a href="#">@{{ tweet?.authorTwitterHandle }}</a>
    </div>

    <p class="mb-4 text-lg" v-html="processedTweetContent"></p>

    <div
      v-if="tweet?.previewImageUrls?.length"
      class="flex flex-wrap gap-2 my-6"
    >
      <img
        v-for="image in (tweet?.previewImageUrls ?? []).slice(0, 4)"
        :key="image"
        :src="image"
        class="rounded-2xl w-24 lg:w-32 aspect-square object-cover"
        alt=""
      />
    </div>

    <!-- Tweet threads -->
    <div
      v-if="tweet.tweetType === AnalyticsTweetType.TweetstormStart"
      class="my-4"
    >
      <div class="flex items-center justify-between w-full text-black/50">
        <span class="flex items-center text-sm gap-2"
          ><ForumIcon />This post is a Thread</span
        >
        <IconsChevBotIcon />
      </div>
    </div>

    <div
      class="flex flex-wrap gap-4 justify-between items-center text-black/50 fill-black/50"
    >
      <button class="hover:underline" @click="copyTweetLink">
        <TimeAgo v-if="tweet && tweet.datePosted" :date="tweet?.datePosted" />
        via {{ tweet?.origin == "x" ? "X.com" : "Evangelist" }}
        <tippy to="parent" :hide-on-click="false">
          {{
            tweetLinkCopied
              ? "Post link copied!"
              : "Copy post link to clipboard"
          }}
        </tippy>
      </button>
      <div class="flex items-center gap-4">
        <span class="flex items-center gap-2">
          <EyeIcon />
          <span>{{ tweet?.numImpressions }}</span>
        </span>
        <span class="flex items-center gap-2">
          <IconsChatIcon />
          <span>{{ tweet?.numReplies }}</span>
        </span>
        <span class="flex items-center gap-2">
          <HeartIcon />
          <span>{{ tweet?.numLikes }}</span>
        </span>
      </div>
    </div>

    <div class="border-t borfer-black/10 my-8"></div>
    <TweetDetailReward
      v-if="!isLoading && campaign"
      :tweet="tweet"
      :hide-relevance="state === 'eligible-bonus'"
      :campaign="campaign"
    />

    <div class="my-8 format-text">
      <div v-if="state == 'evaluating'">
        <p>
          Evangelist algorithm is evaluating this post for its qualities, and
          its relevance score is being calculated. If the post reaches a
          relevance threshold, it will become eligible for a reward.
        </p>
        <p>
          It can take up to 24 hours. In a meanwhile you can snoop around to see
          more campaigns you might be interested in.
        </p>

        <div class="mt-8">
          <NuxtLink
            to="/campaigns"
            data-analytics-click-id="button_post_detail_sidebar_visit_all_campaigns_clicked"
            class="btn bg-purple-light hover:bg-black hover:text-white"
            >Visit All Campaigns</NuxtLink
          >
        </div>
      </div>
      <div v-else-if="state == 'eligible-bonus'">
        <p>
          This post has passed the campaign criteria and is eligible for the
          reward. It will appear in your balance right away, once you claim it.
        </p>
        <p>
          Claim your reward before it expires in
          <ExpiresIn v-if="campaign" :date="campaign?.dateEnd" />.
        </p>
        <div class="mt-8">
          <PostClaimButton
            class="btn bg-purple-light hover:bg-black hover:text-white mb-2 lg:mb-10"
            :post="tweet"
            @claimTweet="getTweet"
          />
        </div>
      </div>
      <div v-else-if="state == 'eligible'">
        <p v-if="campaign && campaign.apyBonus">
          This post has passed the criteria of the campaign so now you are
          eligible for +1% APY boost on your verified wallet. Visit campaign
          details to learn more.
        </p>
        <p v-if="campaign && !campaign.apyBonus">
          This post has passed the campaign criteria and is eligible for the
          reward. The final reward will be calculated based off your
          {{ getUserTopNText(campaign) }} most impactful posts, and sent to you
          at the end of the campaign evaluation on
          {{ formatNiceDate(campaign?.dateMaturity) }}.
        </p>
        <p v-if="campaign && !campaign.apyBonus">
          Claim your reward before it expires in
          <ExpiresIn :date="campaign?.dateEnd" />.
        </p>
        <div v-if="campaign && !campaign.apyBonus" class="mt-8">
          <PostClaimButton
            class="btn bg-purple-light hover:bg-black hover:text-white mb-2 lg:mb-10"
            :post="tweet"
            @claimTweet="getTweet"
          />
        </div>
      </div>
      <div v-else-if="state == 'not-eligible'">
        <p>
          This post did not pass the criteria of any ongoing campaigns so it is
          not eligible for a reward. Good starting point could be to visit
          <NuxtLink class="text-purple hover:underline" to="/campaigns"
            >All Campaigns</NuxtLink
          >, pick one and follow the advertiser pointers there.
        </p>
        <div class="mt-8">
          <NuxtLink
            to="/campaigns"
            class="btn bg-purple-light hover:bg-black hover:text-white"
            >Visit All Campaigns</NuxtLink
          >
        </div>
      </div>
      <div v-else-if="state == 'claimed'">
        <p v-if="campaign">
          This post has passed the campaign criteria and is eligible for the
          reward. The final reward will be calculated based off your
          {{ getUserTopNText(campaign) }} most impactful posts, and sent to you
          at the end of the campaign evaluation on
          {{ formatNiceDate(campaign?.dateMaturity) }}.
        </p>
      </div>
      <div v-else-if="state == 'rewarded'">
        <p>
          This post has passed the campaign criteria and was eligible for the
          reward as one your {{ getUserTopNText(campaign) }} most impactful
          posts. The final reward is available for you to move to Evangelist.
        </p>
      </div>
      <div v-else-if="state == 'paid'">
        <p>
          This post has passed the campaign criteria and was eligible for the
          reward as one of your {{ getUserTopNText(campaign) }} most impactful
          posts and it was already claimed.
        </p>
      </div>
      <div v-else-if="state == 'paid-bonus'">
        <p>
          This post has passed the campaign criteria and it was already claimed.
        </p>
      </div>
      <div v-else-if="state == 'not-rewarded'">
        <p>
          Although this post passed the campaign criteria, it was not your
          {{ getUserTopNText(campaign) }}
          most impactful posts within that week, so it was not rewarded.
        </p>
        <div class="mt-8">
          <NuxtLink
            to="/campaigns"
            class="btn bg-purple-light hover:bg-black hover:text-white"
            >Visit All Campaigns</NuxtLink
          >
        </div>
      </div>
      <div v-else-if="state == 'expired'">
        <p>
          The campaign has already ended without any claims on this post.
          Therefore it has not been included in the evaluation period.
        </p>
        <div class="mt-8">
          <NuxtLink
            to="/campaigns"
            class="btn bg-purple-light hover:bg-black hover:text-white"
            >Visit All Campaigns</NuxtLink
          >
        </div>
      </div>
      <div v-else-if="state == 'expired-bonus'">
        <p>This post has not been claimed in time.</p>
        <div class="mt-8">
          <NuxtLink
            to="/campaigns"
            class="btn bg-purple-light hover:bg-black hover:text-white"
            >Visit All Campaigns</NuxtLink
          >
        </div>
      </div>
    </div>
  </div>

  <div v-if="tweet && campaigns.length" class="border-t border-black/10 py-10">
    <div class="text-black/50 mb-8">
      Eligible {{ campaigns.length === 1 ? "Campaign" : "Campaigns" }}
      <span v-if="campaigns.length > 1"> ({{ campaigns.length }}) </span>
    </div>
    <div v-for="(c, i) in campaigns" :key="c.campaignWeekId">
      <div
        v-if="c.campaignWeekId === forCampaignWeekId"
        class="block border border-black/10 bg-gradient-to-b from-white to-grey-light/20 hover:bg-gradient-to-b hover:from-white hover:to-white hover:shadow-blur rounded-2xl mb-3 p-6 pt-5 font-semibold cursor-pointer"
        @click="close"
      >
        <div class="text-lg mb-4 line-clamp-1">{{ c.title }}</div>
        <CampaignFooter
          :is-highest-reward="isHighestRewardedCampaign(i)"
          :campaign="c"
        />
      </div>
      <NuxtLink
        v-if="c.campaignWeekId !== forCampaignWeekId"
        :to="`/campaign/${c.campaignWeekId}`"
        class="block border border-black/10 bg-gradient-to-b from-white to-grey-light/20 hover:bg-gradient-to-b hover:from-white hover:to-white hover:shadow-blur rounded-2xl mb-3 p-6 pt-5 font-semibold cursor-pointer"
      >
        <div class="text-lg mb-4 line-clamp-1">{{ c.title }}</div>
        <CampaignFooter
          :is-highest-reward="isHighestRewardedCampaign(i)"
          :campaign="c"
        />
      </NuxtLink>
    </div>
  </div>
</template>
